import * as React from "react";
import { NavigationStep, Page } from "../../types";
import { Link } from "react-router-dom";
import ActionDropdown from "./ActionsDropdown";
import DobAgeProvider from "../../../components/DobAgeProvider";
import { debounce } from "throttle-debounce";
import Select from "react-select";
import { User } from "../../session/types";
import { Organization } from "../../organizations/types";
import { Patient } from "../../patient/types";

interface OwnProps {
  user: User | null;
  patientList: Page<Patient>;
  searchString: string;
  selectedPatient: Patient | null;
  showInactive: boolean;
  locations: string[];
  selectedLocation: string;
  passiveLoading: boolean;
  orgs: Organization[] | null;
  selectedOrganization: Organization | null;
  getPatientList: () => void;
  getMorePatients: () => void;
  setPatientAndAct: (patient: Patient, step: NavigationStep) => void;
  setSearchString: (searchString: string) => void;
  setShowInactive: (showInactive: boolean) => void;
  deactivatePatient: (patient: Patient) => void;
  getLocations: (showInactive: boolean) => void;
  setLocation: (searchString: string) => void;
}

interface OwnState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debouncedSearch: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setLocationDropdown: any;
}

type Props = OwnProps;

class Overview extends React.PureComponent<Props, OwnState> {
  public constructor(props: Props) {
    super(props);

    this.state = {
      debouncedSearch: debounce(400, false, this.props.getPatientList),
      setLocationDropdown: null
    };

    this.patientSearchStringChange = this.patientSearchStringChange.bind(this);
    this.handleInactiveChange = this.handleInactiveChange.bind(this);
  }

  public componentDidMount() {
    if (this.props.selectedLocation) {
      this.setState({
        setLocationDropdown: {
          value: this.props.selectedLocation,
          label: this.props.selectedLocation
        }
      });
    }
  }

  componentDidUpdate() {
    const selectedLocation = this.props.selectedLocation;
    const locations = this.props.locations;
    if (selectedLocation && !locations.find(l => l == selectedLocation)) {
      this.setState({
        setLocationDropdown: null
      });
      this.props.setLocation("");
      this.props.getPatientList();
    }
  }

  private readonly patientSearchStringChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.setSearchString(e.target.value);
    this.state.debouncedSearch();
  };

  private handleInactiveChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.props.setShowInactive(e.target.checked);
    this.props.getPatientList();
    this.props.getLocations(e.target.checked);
  }

  private getMorePatients = () => {
    this.props.getMorePatients();
  };

  private locationOptions = () => {
    return this.props.locations.map(location => {
      return { value: location, label: location };
    });
  };

  private handleSelectedLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e) {
      this.setState({
        setLocationDropdown: e
      });
      this.props.setLocation(e.value);
      this.props.getPatientList();
    } else {
      this.setState({
        setLocationDropdown: null
      });
      this.props.setLocation("");
      this.props.getPatientList();
    }
  };

  public render() {
    const {
      searchString,
      patientList,
      setPatientAndAct,
      showInactive,
      deactivatePatient,
      locations,
      passiveLoading,
      user,
      selectedOrganization
    } = this.props;
    return (
      <>
        <div className="patients-web">
          <div className="section mb-15 pt-15">
            <div className="patients">
              <div className="flex-between-center ml-15 mr-15">
                <div className="title">Patients {user?.featureFlags?.enableVideo}</div>
                <div className="search-container">
                  <input
                    id="patientSearchString"
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    defaultValue={searchString}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    onChange={this.patientSearchStringChange}
                  />
                  <i className="fas fa-search search-icon" />
                </div>
                {locations && (
                  <div>
                    <Select
                      className="location-select"
                      placeholder="Location Filter"
                      onChange={this.handleSelectedLocationChange}
                      options={this.locationOptions()}
                      value={this.state.setLocationDropdown}
                      isClearable={true}
                    />
                  </div>
                )}
                <div className="flex-center">
                  <input
                    id="inActive"
                    type="checkbox"
                    className="inactive-checkbox"
                    defaultChecked={showInactive}
                    onChange={this.handleInactiveChange}
                  />
                  <div>Show Inactive</div>
                </div>
              </div>

              <div className="patient header mt-20">
                <div>Last Name</div>
                <div>First Name</div>
                <div>
                  Date of Birth <span className="divider" /> Age
                </div>
                <div className="center">Actions</div>
              </div>

              {!passiveLoading &&
                patientList &&
                patientList.results.map(patient => (
                  <div className="patient" key={patient.id}>
                    <div>{patient.familyName}</div>
                    <div>{patient.givenName}</div>
                    <div>
                      <div className="ml-15">
                        {patient.dateOfBirth && <DobAgeProvider dob={patient.dateOfBirth} />}
                      </div>
                    </div>
                    <div className="center">
                      <ActionDropdown
                        patient={patient}
                        setPatientAndAct={setPatientAndAct}
                        deactivatePatient={deactivatePatient}
                        user={user}
                        organization={selectedOrganization}
                      />
                    </div>
                  </div>
                ))}

              {!passiveLoading && this.props.patientList.next && (
                <button className="m-15" onClick={this.getMorePatients}>
                  Show More Patients
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="patients-mobile">
          <div className="pl-15 pr-15">
            <div className="flex-between-center mt-10 mb-10">
              <div className="title">Patients</div>
              <div className="flex-center">
                <input
                  id="inActive"
                  type="checkbox"
                  className="inactive-checkbox"
                  checked={showInactive}
                  onChange={this.handleInactiveChange}
                />
                <div>Show Inactive</div>
              </div>
            </div>

            <div>
              <input
                id="mobilesearchstring"
                type="text"
                className="form-control"
                placeholder="Search"
                defaultValue={searchString}
                onChange={this.patientSearchStringChange}
              />
            </div>

            <div className="patients mt-10">
              {patientList &&
                patientList.results.map(patient => (
                  <Link to={`/patient/${patient.id}/`} key={patient.id}>
                    <div className="patient">
                      <div>
                        {patient.familyName}, {patient.givenName}
                      </div>
                      <div>
                        {patient.dateOfBirth && <DobAgeProvider dob={patient.dateOfBirth} />}
                      </div>
                    </div>
                  </Link>
                ))}
            </div>

            {!passiveLoading && patientList.next && (
              <button className="m-15" onClick={this.getMorePatients}>
                Show More Patients
              </button>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default Overview;
